import { useCurrentUserDangerously } from '@euler/app/flows/auth/context';
import QrcodeSharingModal from '@euler/app/flows/settings/components/QrcodeSharingModal';
import { useAppSharing } from '@euler/app/flows/settings/functions';
import { useIsWeixinAvailable } from '@euler/functions/useIsWeixinAvailable';
import { onErrorAlert } from '@euler/utils';
import {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';
import { Platform, Share } from 'react-native';
import Toast from 'react-native-root-toast';
import * as WeChat from 'react-native-wechat-lib';

export type AppSharingModalRef = {
  show: () => void;
};

const buildAppStoreLink = (
  type: 'universal' | 'applink',
  {
    appId,
    market = 'cn',
  }: {
    appId: string;
    market?: string;
  },
) => {
  const protocol = type === 'universal' ? 'https' : 'itms-apps';

  // all the following url schemes work:
  // https://apps.apple.com/cn/app/id522700349
  // itms-apps://apps.apple.com/app/id522700349
  // itms-apps://itunes.apple.com/app/id522700349
  // in general, it follows the format like:
  // [https|itms-apps]://[itunes|apps].apple.com(/<mkt>)?/app(<appname>/)?/id<appid>(?l=<lang>)?

  return `${protocol}://apps.apple.com/${market}app/id${appId}`;
};

export const AppSharingModal = memo(
  forwardRef<AppSharingModalRef, any>((_, ref) => {
    const [visible, setVisible] = useState(false);
    const isWeixinAvailable = useIsWeixinAvailable();
    const { appSharingInfo, prepareAppSharingInfo } = useAppSharing();
    const profile = useCurrentUserDangerously();

    useImperativeHandle(ref, () => ({
      show: () => {
        prepareAppSharingInfo()
          .then(result => {
            if (result != null) {
              if (Platform.OS !== 'ios' || result.ios.url) {
                setVisible(true);
              } else {
                const link = buildAppStoreLink('universal', {
                  appId: result.ios.appStore.appId,
                  market: result.ios.appStore.market,
                });
                void Share.share({
                  url: link,
                  title: result.title,
                  message: result.message,
                });
              }
            }
          })
          .catch(() => null);
      },
    }));

    const onClose = useCallback(() => setVisible(false), []);

    let url = '';
    if (appSharingInfo != null) {
      if (Platform.OS == 'ios') {
        url = appSharingInfo.ios.url ?? '';
      } else if (Platform.OS === 'android') {
        url = appSharingInfo.android.url;
      } else {
        url = appSharingInfo.web.url;
      }
    }

    const onShareToWeChat = useCallback(async () => {
      if (!appSharingInfo) return;

      if (!(await WeChat.isWXAppInstalled())) {
        Toast.show('未安装微信', {
          duration: 1000,
          position: Toast.positions.BOTTOM,
        });
        return;
      }
      WeChat.shareWebpage({
        title: appSharingInfo.title,
        webpageUrl: url,
        description: appSharingInfo.message,
        thumbImageUrl:
          appSharingInfo.imageUrl ??
          profile.org.logoImgUrl ??
          profile.org.iconImgUrl,
      }).catch(onErrorAlert);
    }, [appSharingInfo, profile.org.iconImgUrl, profile.org.logoImgUrl, url]);

    return (
      <QrcodeSharingModal
        title="扫一扫安装知车智检"
        url={url}
        iconUrl={profile.org.logoImgUrl ?? profile.org.iconImgUrl}
        isVisible={visible}
        onClose={onClose}
        onShareToWechat={isWeixinAvailable ? onShareToWeChat : undefined}
        textForCopy={appSharingInfo?.textForCopy?.replace('${url}', url)}
      />
    );
  }),
);
